import {
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Spinner,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import { mimChatbotsApi } from 'apis/chatbots';
import { usersApi } from 'apis/users';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { ChatbotIdentity } from '../components/chatbotIdentity/chatbotIdentity';

import styles from './chatbotEditIdentity.module.scss';

const formId = 'mim-chatbot-identity';

type FieldValues = {
  name: string;
  userName: string;
  userAvatarUrl: string;
};

export const ChatbotEditIdentityModal = createAppModal(
  { type: 'settings:editChatbotIdentityModal' },
  ({ isOpen, hide, modal }): JSX.Element => {
    const chatbot = modal.arg.chatbot;

    const [updateChatbot, { isLoading: isUpdating }] =
      mimChatbotsApi.useUpdateMimChatbotIdentityMutation();

    const { data, isLoading } = usersApi.useReadUserQuery(
      {
        id: chatbot?.systemUserId ?? '',
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

    const navigate = useAppNavigate();
    const notify = useNotifications();

    const form = useForm<FieldValues>({
      defaultValues: {
        name: chatbot.name,
        userName: data?.data?.name || '',
        userAvatarUrl: data?.data?.avatar_url || '',
      },
      mode: 'all',
    });

    useEffect(() => {
      if (!isLoading) {
        form.reset({
          name: chatbot.name,
          userName: data?.data?.name || '',
          userAvatarUrl: data?.data?.avatar_url || '',
        });
      }
    }, [form, chatbot, data, isLoading]);

    const onSubmit: SubmitHandler<FieldValues> = async ({
      name,
      userName,
      userAvatarUrl,
    }) => {
      try {
        const response = await updateChatbot({
          id: chatbot.id,
          name,
          user: {
            name: userName,
            ...(userAvatarUrl !== '' ? { avatarUrl: userAvatarUrl } : {}),
          },
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${response.data?.name || 'Chatbot'}" has been updated.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );
          hide?.();
          ampli.chatbotIdentityEdited();
          navigate({ path: '/settings/chatbots/mim' });
        }
      } catch (error) {
        notify(
          {
            title: 'Error while updating chatbot.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Edit chatbot identity" onClose={hide}></ModalHead>
        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {isLoading ? (
              <FlexBox
                justifyContent="center"
                alignItems="center"
                height="100px"
              >
                <Spinner size={32} />
              </FlexBox>
            ) : (
              <FormProvider {...form}>
                <ChatbotIdentity />
              </FormProvider>
            )}
          </form>
        </ModalBody>
        <ModalFoot
          confirmName="Update"
          confirmOnClick={noop}
          confirmProps={{
            isLoading: isUpdating,
            loadingIndicator: 'Updating...',
            form: formId,
            type: 'submit',
            disabled:
              isLoading ||
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              !form.formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
